/**
 * Category-related data hooks.
 *
 * Top-level categories live at level 1 in the DB (the seed wraps everything
 * under a synthetic `all` root at level 0), so a category path always looks
 * like `all/<top-level>/<sub>/<leaf>`.
 *
 * To find entities tagged anywhere under a given category we use the
 * materialized-path trick: the entity model already denormalizes
 * `categoriesPaths: string[]`, so a single indexed regex matches the node and
 * every descendant. See `buildCategoryWhere` below.
 */
import { useQuery } from '@apollo/client';
import {
  TOP_CATEGORIES,
  CATEGORIES_BY_PARENT,
} from '@/graphql/queries';
import { usePreloadedQuery } from '@/hooks/routePreload';
import { useRequestCountry } from '@/hooks/useRouteLocale';
import { topCategoriesVariables } from '@/seo/categoryHubs';
import type { Category } from '@/types';

export interface CategoryListState {
  items: Category[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export interface CategoryDetailState {
  item: Category | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Level-1 categories for the home strip and the cascading picker root. */
export function useTopCategories(): CategoryListState {
  // The home route module's server copy answers first (web plan W4.5).
  const country = useRequestCountry();
  const { data, loading, error, refetch } = usePreloadedQuery<{ topCategories?: Category[] | null }>(
    TOP_CATEGORIES,
    { variables: topCategoriesVariables(country) },
  );
  return {
    items: data?.topCategories ?? [],
    loading,
    error: error?.message ?? null,
    refetch: () => {
      void refetch();
    },
  };
}

/** Immediate children of a parent category (pass null for level-1). */
export function useCategoryChildren(parentId: string | null): CategoryListState {
  const country = useRequestCountry();
  const { data, loading, error, refetch } = useQuery<{
    categoriesByParent?: Category[] | null;
  }>(CATEGORIES_BY_PARENT, {
    variables: { parentId, ...(country ? { country } : {}) },
  });
  return {
    items: data?.categoriesByParent ?? [],
    loading,
    error: error?.message ?? null,
    refetch: () => {
      void refetch();
    },
  };
}


// ──────────────────────────────────────────────────────────────────
// Filter / sort builders for the published-list queries
// ──────────────────────────────────────────────────────────────────

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export interface BuildWhereInput {
  /** Free-text name search (case-insensitive). */
  query?: string | null;
  /** Match any entity tagged on this path *or anywhere underneath it*. */
  categoryPath?: string | null;
  /** Alternative: match an exact category id (leaf-only). */
  categoryId?: string | null;
}

/**
 * Build the JSON-stringified Mongo `where` clause expected by the backend's
 * `Options.where` field. Returns `undefined` when no filters apply.
 *
 * Matching semantics for `categoryPath` — three buckets, all combined via `$or`:
 *
 *   1. EXACT       — entity tagged at exactly this node.
 *   2. ANCESTOR    — entity tagged at a broader parent (e.g. brand tagged
 *                    "Fashion & Apparel" should match when the shopper drills
 *                    into "Women's Clothing", because the brand's coverage
 *                    by definition includes its descendants).
 *   3. DESCENDANT  — entity tagged at a more specific child (e.g. a "Milk"
 *                    promo should match when the shopper browses "Dairy &
 *                    Eggs" or "Groceries & Supermarket").
 *
 * 1+2 share an index-friendly `$in` against the precomputed ancestor list of
 * the search path. 3 uses a left-anchored regex against `categoriesPaths`
 * (which is denormalized on every entity at write-time).
 */
export function buildCategoryWhere(input: BuildWhereInput): string | undefined {
  const clauses: Record<string, unknown>[] = [];

  if (input.query && input.query.trim()) {
    clauses.push({
      name: { $regex: escapeRegex(input.query.trim()), $options: 'i' },
    });
  }

  if (input.categoryPath && input.categoryPath.trim()) {
    const p = input.categoryPath.trim();

    // Build every ancestor path of the search node, including itself.
    // E.g. "all/fashion-and-apparel/womens-clothing" →
    //   ["all", "all/fashion-and-apparel", "all/fashion-and-apparel/womens-clothing"].
    const segments = p.split('/').filter(Boolean);
    const ancestorPaths: string[] = [];
    for (let i = 1; i <= segments.length; i += 1) {
      ancestorPaths.push(segments.slice(0, i).join('/'));
    }

    clauses.push({
      $or: [
        // Exact match OR ancestor (broader) tag.
        { categoriesPaths: { $in: ancestorPaths } },
        // Descendant (more specific) tag.
        { categoriesPaths: { $regex: `^${escapeRegex(p)}/` } },
      ],
    });
  } else if (input.categoryId) {
    clauses.push({ categories: { $in: [input.categoryId] } });
  }

  if (clauses.length === 0) return undefined;
  if (clauses.length === 1) return JSON.stringify(clauses[0]);
  return JSON.stringify({ $and: clauses });
}

export type SortKey =
  | 'popular' // viewCounts desc
  | 'newest' // createdAt desc
  | 'name' // name asc
  | 'subscribers'; // subscriberCounts desc

/** Translate a SortKey into the JSON-stringified Mongo sort the backend expects. */
export function buildSort(sort: SortKey): string {
  switch (sort) {
    case 'popular':
      return JSON.stringify({ viewCounts: -1 });
    case 'newest':
      return JSON.stringify({ createdAt: -1 });
    case 'name':
      return JSON.stringify({ name: 1 });
    case 'subscribers':
      return JSON.stringify({ subscriberCounts: -1 });
    default:
      return JSON.stringify({});
  }
}
